import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Radio, RadioChangeEvent, StepProps } from 'antd5';
import { Steps as ProgressSteps, useMessage } from 'components/ui';
import {
  InspectionTypeSelector,
  ModalBody,
  StyledModal,
  TitleContainer,
} from './UploadInspectionDataModal.style';

import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import {
  SingleInspection,
  SingleInspectionValidation,
  SingleInspectionValidationProps,
} from './SingleInspection';
import {
  MultipleInspections,
  MultipleInspectionValidation,
  MultipleInspectionValidationProps,
} from './MultipleInspections';
import { ImageDetails } from './ImageDetails';
import { CancelUploadModal } from './CancelUploadModal';
import { ConfirmUploadModal } from './ConfirmUploadModal';
import {
  AtlasGqlInspectionTask,
  AtlasGqlInternalBladeInspectionTask,
  AtlasGqlAsset,
  useBulkDeleteInspectionsMutation,
  useDeleteTasksMutation,
  AtlasGqlInspectionType,
} from 'types/atlas-graphql';

interface UploadInspectionDataModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  initialStep?: InitialStep;
  initialInspectionType?: AtlasGqlInspectionType;
  lockedUploadType?: UploadTypes;
  existingInspectionIds?: string[];
  inspectionTasks?: {
    taskId: string;
    inspectionId?: string;
  }[];
  requireTaskId?: boolean;
  task?: (AtlasGqlInspectionTask | AtlasGqlInternalBladeInspectionTask) | null;
  asset?: AtlasGqlAsset | null;
  onSingleInspectionCreate?: () => void;
}

export enum UploadTypes {
  Single,
  Multiple,
}

export enum Steps {
  SingleUpload,
  SingleUploadValidation,
  SummaryUpload,
  SummaryMapping,
  SummaryPreview,
  SummaryValidation,
  DetailsUpload,
  DetailsMapping,
  DetailsPreview,
  DetailsValidation,
  ImageUpload,
  ImageUploadValidation,
}
export type InitialStep = Steps.SingleUpload | Steps.SummaryUpload | Steps.DetailsUpload;

export type InspectionUploadWorkflowComponentProps = {
  uploadId: string;
  requireTaskId?: boolean;
  step: Steps;
  setStep: Dispatch<SetStateAction<Steps>>;
  setUploadErrors: Dispatch<
    SetStateAction<{
      summary: boolean;
      imageDetails: boolean;
    }>
  >;
  setCreatedInspectionIds: Dispatch<SetStateAction<string[]>>;
  setCreatedTaskIds: Dispatch<SetStateAction<string[]>>;
  inspectionTasks?: UploadInspectionDataModalProps['inspectionTasks'];
  onComplete?: () => void;
};

export const UploadInspectionDataModal: React.FunctionComponent<UploadInspectionDataModalProps> = ({
  open,
  onClose,
  onComplete,
  onSingleInspectionCreate,
  initialStep,
  initialInspectionType,
  lockedUploadType,
  existingInspectionIds,
  inspectionTasks,
  requireTaskId,
  task,
  asset,
}) => {
  const message = useMessage();

  // uploadId for entire process created up top; should be stable for inspection and image details files
  const uploadId = useRef<string>(uuid());

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [step, setStep] = useState<Steps>(
    initialStep ?? lockedUploadType === UploadTypes.Multiple
      ? Steps.SummaryUpload
      : Steps.SingleUpload
  );
  const [pendingStep, setPendingStep] = useState<Steps | undefined>();
  const [uploadErrors, setUploadErrors] = useState<{
    summary: boolean;
    imageDetails: boolean;
  }>({ summary: false, imageDetails: false });
  const [uploadType, setUploadType] = useState<UploadTypes>(lockedUploadType ?? UploadTypes.Single);
  const [inspectionType, setInspectionType] = useState<AtlasGqlInspectionType>();
  const [createdInspectionIds, setCreatedInspectionIds] = useState<string[]>([]);
  const [createdTaskIds, setCreatedTaskIds] = useState<string[]>([]);
  const [singleValidationStepProps, setSingleValidationStepProps] =
    useState<Omit<SingleInspectionValidationProps, 'setStep' | 'setPendingStep' | 'rollback'>>();
  const [multipleValidationStepProps, setMultipleValidationStepProps] =
    useState<
      Omit<
        MultipleInspectionValidationProps,
        'setStep' | 'setPendingStep' | 'setUploadErrors' | 'rollback'
      >
    >();
  const [rollbackLoading, setRollbackLoading] = useState<boolean>(false);

  const { refetch } = useServerDataTableContext().context;

  const [deleteInspections] = useBulkDeleteInspectionsMutation();
  const [deleteTasks] = useDeleteTasksMutation();

  const { title, currentStep, stepItems } = useMemo(() => {
    const title = {
      [Steps.SingleUpload]: 'Create Inspection',
      [Steps.SingleUploadValidation]: 'Create Inspection',
      [Steps.SummaryUpload]: 'Upload Inspection Summary File',
      [Steps.SummaryMapping]: 'Upload Inspection Summary File',
      [Steps.SummaryPreview]: 'Upload Inspection Summary File',
      [Steps.SummaryValidation]: 'Upload Inspection Summary File',
      [Steps.DetailsUpload]: 'Upload Image Details File',
      [Steps.DetailsMapping]: 'Upload Image Details File',
      [Steps.DetailsPreview]: 'Upload Image Details File',
      [Steps.DetailsValidation]: 'Upload Image Details File',
      [Steps.ImageUpload]: 'Upload Inspection Images',
      [Steps.ImageUploadValidation]: 'Upload Images',
    }[step];

    /**
     * Rendered steps are conditional based on the initial step. Currently, only
     * SingleUpload, SummaryUpload, and DetailsUpload are supported as initial steps.
     * If other entry points are allowed, this will need to be updated.
     */
    const currentStep = (
      initialStep === Steps.DetailsUpload
        ? {
            [Steps.DetailsUpload]: 0,
            [Steps.DetailsMapping]: 0,
            [Steps.DetailsPreview]: 0,
            [Steps.DetailsValidation]: 1,
            [Steps.ImageUpload]: 2,
            [Steps.ImageUploadValidation]: 3,
          }
        : {
            [Steps.SingleUpload]: 0,
            [Steps.SingleUploadValidation]: 1,
            [Steps.SummaryUpload]: 0,
            [Steps.SummaryMapping]: 0,
            [Steps.SummaryPreview]: 0,
            [Steps.SummaryValidation]: 1,
            [Steps.DetailsUpload]: 2,
            [Steps.DetailsMapping]: 2,
            [Steps.DetailsPreview]: 2,
            [Steps.DetailsValidation]: 3,
            [Steps.ImageUpload]: 4,
            [Steps.ImageUploadValidation]: 5,
          }
    )[step];

    const stepLabels =
      initialStep === Steps.DetailsUpload
        ? ['Upload Image Details', 'Validate Image Details', 'Upload Images', 'Review Images']
        : uploadType === UploadTypes.Single
          ? [
              'Create Inspection',
              'Validate Creation',
              'Upload Image Details',
              'Validate Image Details',
              'Upload Images',
              'Review Images',
            ]
          : [
              'Upload Summary',
              'Validate Summary',
              'Upload Image Details',
              'Validate Image Details',
              'Upload Images',
              'Review Images',
            ];

    const stepItems: StepProps[] = stepLabels.map(label => ({
      title: label,
      status:
        (label === 'Validate Summary' && uploadErrors.summary) ||
        (label === 'Validate Image Details' && uploadErrors.imageDetails)
          ? 'error'
          : undefined,
    }));

    return { title, currentStep, stepItems };
  }, [step, initialStep, uploadType, uploadErrors]);

  // set initial step  on init
  useEffect(() => {
    if (initialStep) {
      setStep(initialStep);
    }
  }, [initialStep]);

  // set inspection type on init
  useEffect(() => {
    if (initialInspectionType) {
      setInspectionType(initialInspectionType);
    }
  }, [initialInspectionType]);

  // reset upload errors on upload type change
  useEffect(() => {
    setUploadErrors({ summary: false, imageDetails: false });
  }, [uploadType]);

  // handles deleting created inspections and tasks
  const handleRollback = async (_toastMessage?: string) => {
    if (createdInspectionIds.length > 0 || createdTaskIds.length > 0) {
      setRollbackLoading(true);

      // delete inspections
      if (createdInspectionIds.length > 0) {
        await deleteInspections({
          variables: { input: { ids: createdInspectionIds } },
        });
      }

      // delete tasks
      if (createdTaskIds.length > 0) {
        await deleteTasks({
          variables: { ids: createdTaskIds },
        });
      }

      setRollbackLoading(false);

      // conditonally refetch the inspection list
      refetch && (await refetch());
    }

    // conditonally set toast message
    const toastMessage = _toastMessage
      ? _toastMessage
      : createdInspectionIds.length > 0 && createdTaskIds.length > 0
        ? 'Inspection & Task creation successfully rolled back'
        : createdInspectionIds.length > 0
          ? 'Inspection creation successfully rolled back'
          : undefined;
    toastMessage && message.success(toastMessage);

    // clear created ids
    setCreatedInspectionIds([]);
    setCreatedTaskIds([]);
  };

  // handles the cancelling of the inspection modal
  const handleCancelModal = () => {
    if (createdInspectionIds.length > 0 || createdTaskIds.length > 0) {
      // opens cancellation modal if inspections/tasks created
      setCancelModalOpen(true);
    } else {
      // otherwises closes modal
      handleCloseModal();
    }
  };

  // handles the closing of the inspection modal
  const handleCloseModal = () => {
    setUploadType(lockedUploadType ?? UploadTypes.Single);
    setStep(initialStep ?? Steps.SingleUpload);
    setCreatedInspectionIds([]);
    setCreatedTaskIds([]);

    onClose();
  };

  return (
    <>
      <StyledModal
        data-testid="upload-inspection-data-modal"
        title={
          <TitleContainer>
            {title}
            <br />
            <ProgressSteps items={stepItems} current={currentStep} size="small" itemWidth="130px" />
          </TitleContainer>
        }
        open={open}
        onCancel={handleCancelModal}
        footer={null}
        width={800}
        maskClosable={false}
        destroyOnHidden
      >
        {[Steps.SingleUpload, Steps.SummaryUpload].includes(step) &&
          lockedUploadType === undefined && (
            <InspectionTypeSelector>
              <span>Number of Inspections:</span>
              <Radio.Group
                buttonStyle="solid"
                size="small"
                options={[
                  { label: 'Single', value: UploadTypes.Single },
                  { label: 'Multiple', value: UploadTypes.Multiple },
                ]}
                defaultValue={uploadType}
                optionType="button"
                onChange={({ target: { value } }: RadioChangeEvent) => {
                  setUploadType(value);
                  const step =
                    value === UploadTypes.Multiple ? Steps.SummaryUpload : Steps.SingleUpload;
                  setStep(step);
                }}
              />
            </InspectionTypeSelector>
          )}
        <ModalBody>
          {step === Steps.SingleUpload && (
            <SingleInspection
              onClose={handleCancelModal}
              setInspectionType={setInspectionType}
              step={step}
              setStep={setStep}
              setUploadErrors={setUploadErrors}
              task={task}
              asset={asset}
              onSingleInspectionCreate={onSingleInspectionCreate}
              setValidationStepProps={setSingleValidationStepProps}
              setCreatedInspectionIds={setCreatedInspectionIds}
              setCreatedTaskIds={setCreatedTaskIds}
            />
          )}
          {[Steps.SummaryUpload, Steps.SummaryMapping, Steps.SummaryPreview].includes(step) && (
            <MultipleInspections
              uploadId={uploadId.current}
              requireTaskId={requireTaskId}
              step={step}
              setStep={setStep}
              setUploadErrors={setUploadErrors}
              setValidationStepProps={setMultipleValidationStepProps}
              setCreatedInspectionIds={setCreatedInspectionIds}
              setCreatedTaskIds={setCreatedTaskIds}
              inspectionTasks={inspectionTasks}
            />
          )}
          {step === Steps.SingleUploadValidation && singleValidationStepProps && (
            <SingleInspectionValidation
              {...singleValidationStepProps}
              setStep={setStep}
              setPendingStep={setPendingStep}
              rollback={{ update: handleRollback, loading: rollbackLoading }}
            />
          )}
          {step === Steps.SummaryValidation && multipleValidationStepProps && (
            <MultipleInspectionValidation
              {...multipleValidationStepProps}
              setStep={setStep}
              setPendingStep={setPendingStep}
              setUploadErrors={setUploadErrors}
              rollback={{ update: handleRollback, loading: rollbackLoading }}
              existingInspectionIds={existingInspectionIds}
            />
          )}
          {[
            Steps.DetailsUpload,
            Steps.DetailsMapping,
            Steps.DetailsPreview,
            Steps.DetailsValidation,
          ].includes(step) && (
            <ImageDetails
              uploadId={uploadId.current}
              uploadType={uploadType}
              inspectionIds={[...createdInspectionIds, ...(existingInspectionIds ?? [])]}
              inspectionTasks={inspectionTasks}
              inspectionType={inspectionType}
              step={step}
              setStep={setStep}
              initialStep={initialStep}
              setUploadErrors={setUploadErrors}
              handleCloseModal={handleCloseModal}
              onComplete={onComplete}
            />
          )}
          {[Steps.ImageUpload, Steps.ImageUploadValidation].includes(step) && (
            <div>TODO: Image Upload sc-125555</div>
          )}
        </ModalBody>
      </StyledModal>
      <CancelUploadModal
        open={cancelModalOpen}
        handleContinue={() => setCancelModalOpen(false)}
        handleCancel={() => {
          setCancelModalOpen(false);
          handleCloseModal();
        }}
        rollback={{ update: handleRollback, loading: rollbackLoading }}
      />
      <ConfirmUploadModal
        open={!!pendingStep}
        handleContinue={() => {
          pendingStep && setStep(pendingStep);
          setPendingStep(undefined);
        }}
        handleCancel={() => setPendingStep(undefined)}
      />
    </>
  );
};
